/**
 * Deadlines, in two time zones, because the league is not in one.
 *
 * The rulebook says "5:00pm" and has never said where. Tim is in Colorado, most of the league
 * is on the east coast, and every year somebody submits at 4:45 their time and finds out that
 * was 6:45 somewhere else. So: deadlines are stored as ISO instants — one true moment — and
 * always DISPLAYED in both Mountain and Eastern, with the authoritative zone named.
 *
 * Eastern is the authoritative zone. That is not in the book either; it is what the league has
 * actually run on, and writing it down here is cheaper than another argument.
 */

/** The zone a deadline is quoted in when somebody says "5pm". */
export const LEAGUE_ZONE = 'America/New_York';

export const ZONES: ReadonlyArray<{ tz: string; label: string }> = [
  { tz: 'America/New_York', label: 'ET' },
  { tz: 'America/Denver', label: 'MT' },
];

const fmt = (iso: string, tz: string, opts: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-US', { ...opts, timeZone: tz }).format(new Date(iso));

/** "Wed Sep 2, 5:00 PM" in one zone. */
export function inZone(iso: string, tz: string): string {
  return fmt(iso, tz, {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

/** Just the clock — "5:00 PM" — for a column that has already said which day it is. */
export function clockIn(iso: string, tz: string): string {
  return fmt(iso, tz, { hour: 'numeric', minute: '2-digit' });
}

/**
 * The calendar day this instant falls on in `tz`, as `YYYY-MM-DD`.
 *
 * Slicing an ISO string gives the UTC day, which is a different day from about 5pm Mountain
 * onwards — so a draft-night action taken at 8:07pm files under tomorrow's date. Anything that
 * groups or labels by day has to ask a zone which day it means.
 */
export function dayIn(iso: string, tz: string): string {
  return fmt(iso, tz, { year: 'numeric', month: '2-digit', day: '2-digit' })
    .replace(/^(\d+)\/(\d+)\/(\d+)$/, '$3-$1-$2');
}

/** The clock alone, 24-hour — "20:07" — for a narrow column beside a day heading. */
export function clock24In(iso: string, tz: string): string {
  return fmt(iso, tz, { hour: '2-digit', minute: '2-digit', hour12: false })
    .replace(/^24:/, '00:');
}

/**
 * "5:00 PM ET Mon, Aug 31" — the clock first, then the day.
 *
 * Backwards from `inZone` on purpose, and it is the shape Pass 06 §3 writes its copy in. These
 * strings land mid-sentence — "expires 5:00 PM ET Mon, Aug 31" — where the deadline is the point
 * and the date is the qualifier. `bothZones` leads with the day because it heads a card.
 *
 * ONE ZONE. The second zone goes in a tooltip at these call sites rather than inline; a sentence
 * carrying two clocks stops being a sentence.
 */
export function etStamp(iso: string): string {
  const z = ZONES[0]!;
  return `${clockIn(iso, z.tz)} ${z.label} ${fmt(iso, z.tz, { weekday: 'short', month: 'short', day: 'numeric' })}`;
}

/**
 * "Wed Sep 2, 5:00 PM ET · 3:00 PM MT" — full date once, then just the clock, because the
 * second zone is a conversion aid and repeating the date makes it read like a second deadline.
 */
export function bothZones(iso: string): string {
  const [primary, ...rest] = ZONES;
  const head = `${inZone(iso, primary!.tz)} ${primary!.label}`;
  const tail = rest.map((z) => {
    const clock = fmt(iso, z.tz, { hour: 'numeric', minute: '2-digit' });
    // A deadline can land on a different calendar day in the other zone. Say so when it does.
    const sameDay = fmt(iso, z.tz, { month: 'short', day: 'numeric' })
      === fmt(iso, primary!.tz, { month: 'short', day: 'numeric' });
    const day = sameDay ? '' : `${fmt(iso, z.tz, { month: 'short', day: 'numeric' })}, `;
    return `${day}${clock} ${z.label}`;
  });
  return [head, ...rest.length ? [tail.join(' · ')] : []].join(' · ');
}

/** Rough, human time remaining. Deliberately coarse — a second-by-second clock is stressful. */
export function timeLeft(iso: string, now = Date.now()): string {
  const left = new Date(iso).getTime() - now;
  if (left <= 0) return 'passed';
  const mins = Math.floor(left / 60_000);
  if (mins < 60) return `${Math.max(1, mins)} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours} hr`;
  return `${Math.floor(hours / 24)} days`;
}

/**
 * Has the preseason finished with us?
 *
 * A deadline that has passed keeps its tile. The instinct is to clear it the moment it shuts, but
 * through August "when was that, and did it actually close?" is asked about as often as "how long
 * have I got", and a row that loses a card every week reads as though something has gone missing.
 *
 * The cutoff is the day AFTER the draft. The draft is the last thing the clock counts to, and a
 * tile reading "closed" the morning after still answers a real question; a week later it is
 * furniture. With no draft date set, nothing is ever cleared — better a stale tile than a screen
 * that empties itself because a field was left blank.
 */
export const PRESEASON_TAIL_MS = 24 * 60 * 60 * 1000;

export function preseasonOver(draftDate: string | null | undefined, now = Date.now()): boolean {
  if (!draftDate) return false;
  const at = new Date(draftDate).getTime();
  return Number.isFinite(at) && now > at + PRESEASON_TAIL_MS;
}

/**
 * The end of the league day `iso` falls in — midnight at the start of the following morning.
 *
 * THE ET DAY, NOT THE UTC DAY, for the same reason `atHourEtOn` is careful about it: an 8pm
 * Eastern meeting is one in the morning the next day in UTC, so anything that reads the calendar
 * date off the raw instant retires the meeting a day early. The calendar is advanced on the
 * date parts rather than by adding 24 hours to a timestamp, so a DST boundary inside the day
 * cannot move the cutoff an hour either way.
 */
export function endOfLeagueDay(iso: string | null | undefined): string | null {
  if (!iso || Number.isNaN(Date.parse(iso))) return null;
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: LEAGUE_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(iso));
  const g = (t: string) => Number(p.find((x) => x.type === t)!.value);
  const next = new Date(Date.UTC(g('year'), g('month') - 1, g('day') + 1));
  const pad = (n: number) => String(n).padStart(2, '0');
  return isoFromZoned(
    `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}T00:00`,
    LEAGUE_ZONE,
  );
}

/**
 * Has the committee meeting's DAY passed?
 *
 * The meeting card is the one card that stops being a card the moment the meeting is over: it is
 * a date, a way in, and an instruction about the lottery, and every one of those is spent. It
 * survives the rest of meeting night on purpose — a man reading it at 11pm still wants the link
 * and the docket — and goes at midnight, which is what "after the day it occurs" means.
 *
 * With no date set nothing is ever retired, the same way `preseasonOver` refuses to empty a
 * screen because a field was left blank.
 */
export function committeeOver(iso: string | null | undefined, now = Date.now()): boolean {
  const end = endOfLeagueDay(iso);
  return end !== null && now >= Date.parse(end);
}

export interface Gate {
  /** Whether a manager may still write. */
  open: boolean;
  label: string;
  /** Inside 24 hours, or already shut. Drives the colour. */
  urgent: boolean;
  /** Set once the moment has passed, whether or not a lock was also applied. */
  passed: boolean;
  /** Set when the gate is open only because a commissioner is holding it so. */
  exception?: GateException;
}

/**
 * A commissioner holding a gate open past its date.
 *
 * THE THIRD STATE (Tim, 3 September). A gate used to be a date and a boolean that could shut it
 * early; nothing could hold it OPEN after the date, so the only way to let the league back in was
 * to move the announced time for everybody. This is that way back in, and it is named as one: the
 * row says who did it and when, the log carries it, and it is never a silent flip.
 *
 * `over` IS THE DEADLINE IT OVERRODE, and it is what makes moving the date clear the exception
 * without anybody remembering to. `exceptionStands` compares it to the deadline in force; if the
 * date has moved since, the exception is spent, on the client and in `firestore.rules` alike.
 */
export interface GateException {
  by: string;
  /** ISO, when it was granted. */
  at: string;
  /** The deadline as it stood when the exception was granted. Null if none was set. */
  over: string | null;
}

/** An exception holds only against the deadline it was granted over. */
export function exceptionStands(
  held: GateException | null | undefined,
  deadline: string | null | undefined,
): held is GateException {
  return Boolean(held) && (held!.over ?? null) === (deadline ?? null);
}

/**
 * One function for every "can I still submit this?" question in the app, so the keeper deadline
 * and the preference deadline can never drift into behaving differently.
 *
 * `locked` is the commissioner's override and beats the clock in both directions: it can shut
 * things early, and leaving it off after the deadline is how one GM gets let back in for a
 * correction without moving the announced time for everybody.
 */
export function gateFor(
  deadline: string | null | undefined,
  locked = false,
  now = Date.now(),
  what = 'Submissions',
  heldOpen?: GateException | null,
): Gate {
  /*
   * HELD OPEN BEATS EVERYTHING, and only against the date it was granted over. A stale exception
   * — the date moved since — is not an exception, and the gate goes back to following the clock.
   */
  if (exceptionStands(heldOpen, deadline)) {
    const passed = Boolean(deadline) && new Date(deadline!).getTime() <= now;
    return {
      open: true,
      label: `${what} held open by ${heldOpen.by}`,
      urgent: false,
      passed,
      exception: heldOpen,
    };
  }
  if (locked) return { open: false, label: `${what} are locked`, urgent: true, passed: false };
  if (!deadline) return { open: true, label: 'No deadline set yet', urgent: false, passed: false };
  const when = bothZones(deadline);
  if (new Date(deadline).getTime() <= now) {
    return { open: false, label: `Deadline passed — ${when}`, urgent: true, passed: true };
  }
  const left = timeLeft(deadline, now);
  return {
    open: true,
    label: `Due ${when} — ${left} left`,
    urgent: new Date(deadline).getTime() - now < 24 * 3_600_000,
    passed: false,
  };
}

/** The three gates the league runs on, by the name the rest of the app uses for each. */
export type GateKey = 'keepers' | 'preferences' | 'proposals';

/** The fields a gate reads off league settings. One place, so a caller cannot pair them wrongly. */
export interface GateSource {
  keeperDeadline?: string | null;
  declarationsLocked?: boolean | null;
  declarationsHeldOpen?: GateException | null;
  preferenceDeadline?: string | null;
  preferencesLocked?: boolean | null;
  preferencesHeldOpen?: GateException | null;
  proposalDeadline?: string | null;
  proposalsLocked?: boolean | null;
  proposalsHeldOpen?: GateException | null;
}

export const GATE_WHAT: Record<GateKey, string> = {
  keepers: 'Declarations',
  preferences: 'Preferences',
  proposals: 'Proposals',
};

/** The settings fields behind each gate, so a form and the rules agree on the names. */
export const GATE_FIELDS: Record<GateKey, {
  deadline: keyof GateSource; locked: keyof GateSource; heldOpen: keyof GateSource;
}> = {
  keepers: { deadline: 'keeperDeadline', locked: 'declarationsLocked', heldOpen: 'declarationsHeldOpen' },
  preferences: { deadline: 'preferenceDeadline', locked: 'preferencesLocked', heldOpen: 'preferencesHeldOpen' },
  proposals: { deadline: 'proposalDeadline', locked: 'proposalsLocked', heldOpen: 'proposalsHeldOpen' },
};

/**
 * `gateFor` with the right three fields read off the settings document.
 *
 * Every screen that asks "may he still write?" goes through here, so the keeper, preference and
 * proposal gates cannot drift into reading different fields — the way `Proposals.tsx` once passed
 * a hard-wired `false` for a lock that did not exist.
 */
export function gateOf(s: GateSource | null | undefined, key: GateKey, now = Date.now()): Gate {
  const f = GATE_FIELDS[key];
  return gateFor(
    s?.[f.deadline] as string | null | undefined,
    Boolean(s?.[f.locked]),
    now,
    GATE_WHAT[key],
    s?.[f.heldOpen] as GateException | null | undefined,
  );
}

/**
 * Build an ISO instant from a wall-clock time in a named zone. `<input type="datetime-local">`
 * hands back local wall-clock with no zone, so without this a commissioner sitting in Denver
 * would silently set every deadline two hours late for the east coast.
 */
export function isoFromZoned(local: string, tz: string): string | null {
  if (!local) return null;
  // Interpret the wall-clock as if it were UTC, then correct by that zone's offset at that
  // moment. Two passes because the offset itself can change across a DST boundary.
  let guess = new Date(`${local}:00Z`).getTime();
  for (let i = 0; i < 2; i++) {
    guess = new Date(`${local}:00Z`).getTime() + (guess - zonedWallClock(guess, tz));
  }
  return new Date(guess).toISOString();
}

/** The inverse: what wall-clock does this instant read as in `tz`, as a UTC-stamped number. */
function zonedWallClock(ms: number, tz: string): number {
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(new Date(ms));
  const g = (t: string) => p.find((x) => x.type === t)!.value;
  const hour = g('hour') === '24' ? '00' : g('hour');
  return Date.parse(`${g('year')}-${g('month')}-${g('day')}T${hour}:${g('minute')}:${g('second')}Z`);
}

/** For `<input type="datetime-local">`: the wall-clock string this instant reads as in `tz`. */
export function zonedInputValue(iso: string | null | undefined, tz: string): string {
  if (!iso) return '';
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(iso));
  const g = (t: string) => p.find((x) => x.type === t)!.value;
  const hour = g('hour') === '24' ? '00' : g('hour');
  return `${g('year')}-${g('month')}-${g('day')}T${hour}:${g('minute')}`;
}

/**
 * How long before the draft the mock stops being available.
 *
 * The mock is a sandbox — its own store, its own picks, nothing saved. That is exactly why it is
 * dangerous half an hour before kickoff: it looks like the draft board, it is one toggle away from
 * the draft board, and a man entering his real first-round pick into it loses it silently and only
 * finds out when somebody else takes the player. Thirty minutes is enough to stop the confusion
 * before people start arriving.
 */
export const MOCK_CLOSES_MINUTES = 30;

export interface MockGate {
  open: boolean;
  /** Said on the toggle, so nobody has to guess why it went away. */
  why: string;
}

export function mockGate(
  draftDate: string | null | undefined,
  now = Date.now(),
  minutes = MOCK_CLOSES_MINUTES,
): MockGate {
  if (!draftDate) return { open: true, why: '' };
  const at = new Date(draftDate).getTime();
  if (Number.isNaN(at)) return { open: true, why: '' };

  const closes = at - minutes * 60_000;
  if (now < closes) return { open: true, why: '' };
  return {
    open: false,
    why: now < at
      ? `Mock drafting closes ${minutes} minutes before the draft — this is the real board now.`
      : 'The draft has started. Everything you enter here is the real thing.',
  };
}

/* ================================================================================ how close it is
 *
 * TOP PROMINENCE IN THE LAST WEEK, A LIVE CLOCK IN THE LAST DAY (Tim, 23 August). Until now every
 * deadline in the app was one of two things — open or shut — and `Gate.urgent` did double duty as
 * "inside a day OR already closed", which is fine for colouring a chip and useless for deciding
 * what a screen leads with. A week out and a month out are not the same fact.
 *
 * Four bands, and each one exists because something on screen changes at it:
 *
 *   far     more than a week away. Lives in the list, in date order, and says nothing loudly.
 *   week    inside seven days. Comes to the top of Home and keeps its own card.
 *   day     inside twenty-four hours. Gets the ticking clock, which is the only place in this app
 *           a number moves once a second.
 *   passed  gone. Still shown — "when was that, and did it close?" is asked all August — but it
 *           has stopped competing for the top of the page.
 */

export type Urgency = 'far' | 'week' | 'day' | 'passed';

export const URGENT_WEEK_MS = 7 * 24 * 3_600_000;
export const URGENT_DAY_MS = 24 * 3_600_000;

export function urgencyOf(iso: string | null | undefined, now = Date.now()): Urgency {
  if (!iso) return 'far';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 'far';
  const left = t - now;
  if (left <= 0) return 'passed';
  if (left <= URGENT_DAY_MS) return 'day';
  if (left <= URGENT_WEEK_MS) return 'week';
  return 'far';
}

/** Sorts the most pressing first: inside a day, then inside a week, then the rest, then the past. */
export const URGENCY_RANK: Readonly<Record<Urgency, number>> = {
  day: 0, week: 1, far: 2, passed: 3,
};

export interface LiveLeft {
  /** Whole units remaining. Days is 0 inside the last day, which is what makes the clock tick. */
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** Total ms remaining, never negative. */
  left: number;
  /** `4:03:12` inside a day; `2 days, 4 hr` outside one. */
  text: string;
  /** Whether `text` is changing every second — the caller only ticks when this is true. */
  ticking: boolean;
}

/**
 * The 24-hour countdown.
 *
 * SECONDS ONLY INSIDE THE LAST DAY. A clock counting down 23 days to the second is a stress
 * machine and a repaint every second for no reason; outside a day this returns the coarse form
 * and says it is not ticking, so Home can subscribe to a one-second interval for exactly as long
 * as it is worth having one.
 *
 * Returns null with no deadline, and a zeroed `passed` value once the moment is gone — the caller
 * decides what a passed deadline looks like, and it is never a clock reading 0:00:00.
 */
export function liveLeft(iso: string | null | undefined, now = Date.now()): LiveLeft | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const left = Math.max(0, t - now);
  const secs = Math.floor(left / 1000);
  const days = Math.floor(secs / 86_400);
  const hours = Math.floor((secs % 86_400) / 3600);
  const minutes = Math.floor((secs % 3600) / 60);
  const seconds = secs % 60;
  const pad = (n: number) => String(n).padStart(2, '0');

  if (left === 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, left: 0, text: 'passed', ticking: false };
  }
  if (left <= URGENT_DAY_MS) {
    return {
      days: 0, hours, minutes, seconds, left,
      text: `${hours}:${pad(minutes)}:${pad(seconds)}`,
      ticking: true,
    };
  }
  return {
    days,
    hours,
    minutes,
    seconds,
    left,
    text: `${days} day${days === 1 ? '' : 's'}${hours ? `, ${hours} hr` : ''}`,
    ticking: false,
  };
}

/* ============================================================== the one deadline a rule fixes ==
 *
 * 2020-II-1 §5: draft-slot preferences "must be submitted by 5:00pm on the day of the Keeper
 * Deadline." Every other date in this app is the commissioners' to choose; this one is written
 * down, and it is set by hand in a field beside the keeper deadline it is supposed to follow.
 *
 * Tim's call of 23 August is that it STAYS a manual field — deriving it would take a control away
 * from a commissioner who may have a reason to move it, and the app inventing a date is worse than
 * the app being wrong about one. So instead of enforcing the rule, this states it: what the rule
 * would put there, whether what is actually there agrees, and by how much. The screens draw that
 * as a hint, never as a refusal.
 *
 * 5:00 PM in the LEAGUE ZONE. The rulebook says "5:00pm" and has never said where; Eastern is what
 * the league has always run on and what `LEAGUE_ZONE` already encodes for everything else.
 */

export const PREF_HOUR_ET = 17;

/** 2020-II-1 §7: the resolved order is published by 8:00 PM on the day rankings were due. */
export const PUBLISH_HOUR_ET = 20;

/**
 * The same wall-clock hour, on the day `iso` falls in — in the league's zone.
 *
 * THE ET DAY, NOT THE UTC DAY. A deadline at 9pm Eastern is one in the morning the next day in
 * UTC, so reading the calendar date off the instant puts everything derived from it twenty-four
 * hours late. Noon deadlines look fine and evening ones are silently wrong, which is the kind of
 * bug that survives a couple of seasons.
 */
export function atHourEtOn(iso: string | null | undefined, hour: number): string | null {
  if (!iso || Number.isNaN(Date.parse(iso))) return null;
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: LEAGUE_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(iso));
  const g = (t: string) => p.find((x) => x.type === t)!.value;
  return isoFromZoned(
    `${g('year')}-${g('month')}-${g('day')}T${String(hour).padStart(2, '0')}:00`,
    LEAGUE_ZONE,
  );
}

/**
 * When the resolved order is due, derived from when rankings are — never a literal.
 *
 * §5 sets submissions at 5:00 PM and §7 publication at 8:00 PM the same evening, so the promise
 * on the draft-order screen moves whenever a commissioner moves the deadline. It used to read
 * "by 8:00 PM the same evening", which is true and says nothing a GM can put in a calendar.
 */
export const publicationDue = (preferenceDeadline: string | null | undefined): string | null =>
  atHourEtOn(preferenceDeadline, PUBLISH_HOUR_ET);

export interface PrefRuleCheck {
  /** What 2020-II-1 §5 puts there, given the keeper deadline. Null when there is no keeper date. */
  expected: string | null;
  /** Whether the set deadline is that instant. True when there is nothing to disagree with. */
  agrees: boolean;
  /** One line for the hint, empty when they agree or when there is nothing to compare. */
  why: string;
}

export function prefRuleCheck(
  keeperDeadline: string | null | undefined,
  preferenceDeadline: string | null | undefined,
): PrefRuleCheck {
  if (!keeperDeadline || Number.isNaN(Date.parse(keeperDeadline))) {
    return { expected: null, agrees: true, why: '' };
  }
  /* 5:00 PM on the keeper deadline's own Eastern day — see `atHourEtOn` for why Eastern. */
  const expected = atHourEtOn(keeperDeadline, PREF_HOUR_ET);
  if (!expected) return { expected: null, agrees: true, why: '' };
  if (!preferenceDeadline || Number.isNaN(Date.parse(preferenceDeadline))) {
    return { expected, agrees: false, why: `2020-II-1 §5 puts this at ${bothZones(expected)}.` };
  }
  const agrees = Date.parse(preferenceDeadline) === Date.parse(expected);
  return {
    expected,
    agrees,
    why: agrees ? '' : `2020-II-1 §5 puts this at ${bothZones(expected)} — 5:00pm on the day of `
      + 'the keeper deadline. This is deliberate if you moved it.',
  };
}
